import "./DestinationTours.css";

import { Controller } from "@hotwired/stimulus";
import {
    browserLocationChangedEventName,
    browserLocationFromEvent,
    readRecentBrowserLocation,
    type SmartGuideBrowserLocation
} from "../BrowserLocation/SmartGuideBrowserLocation";
import { HorizontalRailScroller } from "../HorizontalRail/HorizontalRailDrag";

type DestinationToursModel = {
    tours: DestinationToursModelTour[];
};

type DestinationToursModelTour = {
    id: string;
    index: number;
    latitude?: number | null;
    longitude?: number | null;
};

type TourRowSortItem = {
    row: HTMLElement;
    index: number;
    isNearby: boolean;
    distanceMeters: number;
};

const nearbyTourDistanceMeters = 1000;

export default class DestinationToursController extends Controller<HTMLElement> {
    static values = {
        model: Object
    };

    declare readonly modelValue: DestinationToursModel;

    private railScroller?: HorizontalRailScroller;

    connect(): void {
        this.railScroller = new HorizontalRailScroller({
            root: this.element,
            railSelector: ".destination-thumbnail-rail",
            scrollLeftDataAttribute: "destinationToursScrollLeft"
        });
        this.railScroller.connect();
        this.applyLocationSort(readRecentBrowserLocation());
        document.addEventListener(browserLocationChangedEventName, this.onBrowserLocationChanged);
    }

    disconnect(): void {
        document.removeEventListener(browserLocationChangedEventName, this.onBrowserLocationChanged);
        this.railScroller?.disconnect();
        this.railScroller = undefined;
    }

    private readonly onBrowserLocationChanged = (event: Event): void => {
        this.applyLocationSort(browserLocationFromEvent(event));
    };

    private applyLocationSort(location: SmartGuideBrowserLocation | undefined): void {
        if (!location) {
            return;
        }

        const rowsContainer = this.element.querySelector<HTMLElement>("[data-destination-tours-rows]");
        if (!rowsContainer) {
            return;
        }

        const tours = new Map((this.modelValue.tours ?? []).map(tour => [tour.id, tour]));
        const sortedRows = Array.from(rowsContainer.querySelectorAll<HTMLElement>("[data-destination-tour-row]"))
            .map((row, fallbackIndex): TourRowSortItem => {
                const tour = tours.get(row.dataset.destinationTourId ?? "");
                const distanceMeters = tour?.latitude != null && tour.longitude != null
                    ? distanceInMeters(location.latitude, location.longitude, tour.latitude, tour.longitude)
                    : Number.POSITIVE_INFINITY;

                return {
                    row,
                    index: tour?.index ?? fallbackIndex,
                    isNearby: Number.isFinite(distanceMeters) && distanceMeters <= nearbyTourDistanceMeters,
                    distanceMeters
                };
            })
            .sort((left, right) => {
                if (left.isNearby !== right.isNearby) {
                    return left.isNearby ? -1 : 1;
                }

                if (left.isNearby && right.isNearby && left.distanceMeters !== right.distanceMeters) {
                    return left.distanceMeters - right.distanceMeters;
                }

                return left.index - right.index;
            });

        for (const item of sortedRows) {
            rowsContainer.appendChild(item.row);
        }
    }
}

function distanceInMeters(
    fromLatitude: number,
    fromLongitude: number,
    toLatitude: number,
    toLongitude: number
): number {
    const earthRadiusMeters = 6371000;
    const deltaLatitude = toRadians(toLatitude - fromLatitude);
    const deltaLongitude = toRadians(toLongitude - fromLongitude);
    const fromLatitudeRadians = toRadians(fromLatitude);
    const toLatitudeRadians = toRadians(toLatitude);
    const angle =
        Math.sin(deltaLatitude / 2) * Math.sin(deltaLatitude / 2) +
        Math.cos(fromLatitudeRadians) * Math.cos(toLatitudeRadians) *
        Math.sin(deltaLongitude / 2) * Math.sin(deltaLongitude / 2);

    return earthRadiusMeters * 2 * Math.atan2(Math.sqrt(angle), Math.sqrt(1 - angle));
}

function toRadians(degrees: number): number {
    return degrees * Math.PI / 180;
}
